import React, { useState, useRef, useMemo, useEffect } from "react";
import { Plus, X, Users, Edit2 } from "lucide-react";
import Swal from "sweetalert2";
import JoditEditor from 'jodit-react';

const BecomeAffiliateMember = () => {
  const [content, setContent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    title: "",
    description: ""
  });

  const editorRef = useRef(null);
  const emerald600 = "#059669";
  
  // API base URL - Django backend running on port 8000
  const API_BASE_URL = 'http://127.0.0.1:8000';

  const config = useMemo(() => ({
    readonly: false,
    height: 300,
    theme: "default",
    toolbarButtonSize: "medium",
    removeButtons: ['source', 'fullsize', 'about', 'print', 'preview', 'find'],
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough',
      'ul', 'ol', 'outdent', 'indent',
      'font', 'fontsize', 'brush', 'paragraph',
      'image', 'table', 'link', 'align',
      'undo', 'redo', 'hr', 'eraser', 'copyformat',
      'selectall'
    ],
    style: { background: '#f9fafb' },
    uploader: { insertImageAsBase64URI: true },
    placeholder: 'Enter affiliate membership description here...',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    disablePlugins: ['paste', 'pasteStorage'],
  }), []);

  // Load data from API on component mount
  useEffect(() => {
    fetchAffiliateData();
  }, []);

  const fetchAffiliateData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dla-trade/affiliate-content/current/`);
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      } else if (response.status === 404) {
        setContent(null); // No content exists yet
      } else {
        console.error("Error loading affiliate data:", response.status);
        setContent(null);
      }
    } catch (error) {
      console.error("Error loading affiliate data:", error);
      setContent(null);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setForm({ title: "", description: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title.trim() || !form.description.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill in both title and description.",
        confirmButtonColor: emerald600
      });
      return;
    }

    setSaving(true);

    try {
      let apiUrl;
      let method;
      let bodyData;
      
      if (content && content.id) {
        // Update existing content - use PUT to the detail endpoint
        apiUrl = `${API_BASE_URL}/api/dla-trade/affiliate-content/${content.id}/`;
        method = 'PUT';
        bodyData = {
          title: form.title.trim(),
          description: form.description.trim()
        };
      } else {
        // Create new content - use POST to the list endpoint
        apiUrl = `${API_BASE_URL}/api/dla-trade/affiliate-content/`;
        method = 'POST';
        bodyData = {
          title: form.title.trim(),
          description: form.description.trim()
        };
      }
      
      console.log(`Making ${method} request to:`, apiUrl);
      console.log('Request body:', bodyData);
      
      const response = await fetch(apiUrl, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bodyData)
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const newContent = await response.json();
        setContent(newContent);
        
        Swal.fire({
          icon: "success",
          title: content ? "Updated Successfully!" : "Created Successfully!",
          showConfirmButton: false,
          timer: 1500
        });
        
        closeModal();
      } else {
        // Try to get error details
        let errorMessage = 'Failed to save';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.error || JSON.stringify(errorData);
        } catch (parseError) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error saving data:", error);
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: error.message || "An error occurred while saving. Please try again.",
        confirmButtonColor: emerald600
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    if (content) {
      setForm({
        title: content.title || "",
        description: content.description || ""
      });
      setShowModal(true);
    }
  };

  const handleEditorChange = (newDescription) => {
    setForm(prev => ({ ...prev, description: newDescription }));
  };

  if (loading) {
    return (
      <div className="flex flex-col mt-20 min-h-screen w-full items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        <p className="mt-4 text-gray-600">Loading affiliate content...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      {/* HEADER */}
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">Become Affiliate Member</h1>
            <p className="text-gray-600 mt-1">Manage affiliate membership information</p>
          </div>
          <button
            onClick={!content ? () => setShowModal(true) : handleEdit}
            style={{ backgroundColor: emerald600 }}
            className="flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all"
          >
            {!content ? <><Plus className="w-5 h-5 mr-2" /> Add Content</> : <><Edit2 className="w-4 h-4 mr-2" /> Edit Content</>}
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="px-8 md:px-12 mt-16 w-full pb-20">
        {!content ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
            <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-400 text-lg">No affiliate content added yet.</p>
            <button
              onClick={() => setShowModal(true)}
              style={{ backgroundColor: emerald600 }}
              className="mt-4 px-6 py-2 rounded-lg text-white font-medium hover:opacity-90 transition-all"
            >
              Add Content
            </button>
          </div>
        ) : (
          <div className="animate-fadeIn text-left w-full bg-transparent">
            <div className="mb-10 border-l-4 pl-6" style={{ borderColor: emerald600 }}>
              <h2 className="text-4xl font-bold text-gray-900 tracking-tight">{content.title}</h2>
            </div>
            <div className="mt-12 border-t border-gray-200 pt-10 w-full bg-transparent">
              <div
                className="text-gray-600 text-lg leading-loose space-y-6 custom-description w-full prose prose-emerald max-w-none bg-transparent"
                dangerouslySetInnerHTML={{ __html: content.description || "" }}
              />
              
              {content.updated_at && (
                <div className="mt-12 pt-6 border-t border-gray-100 text-sm text-gray-400">
                  Last updated: {new Date(content.updated_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">{content ? "Update Content" : "Create Content"}</h2>
                <button onClick={closeModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Title
                  </label>
                  <input 
                    value={form.title} 
                    onChange={(e) => setForm({...form, title: e.target.value})} 
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 transition-all" 
                    style={{ '--tw-ring-color': emerald600 }} 
                    required 
                    disabled={saving}
                    placeholder="Enter title for affiliate membership"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Description
                  </label>
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden jodit-editor-container">
                    <JoditEditor
                      ref={editorRef}
                      value={form.description}
                      config={config}
                      onChange={handleEditorChange}
                      onBlur={(newDescription) => {
                        if (newDescription !== form.description) {
                          setForm(prev => ({ ...prev, description: newDescription }));
                        }
                      }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Use the toolbar to format text, add lists, images, links, and more.
                  </p>
                </div>
                <button 
                  type="submit" 
                  style={{ backgroundColor: emerald600 }} 
                  className={`w-full py-4 text-white rounded-2xl font-bold shadow-xl hover:brightness-110 transition-all text-lg flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : content ? 'Update Content' : 'Create Content'}
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        @keyframes modalIn { 
          from { opacity: 0; transform: scale(0.95); } 
          to { opacity: 1; transform: scale(1); } 
        }
        .animate-fadeIn { 
          animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        .animate-modalIn { 
          animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        .custom-description * { 
          background-color: transparent !important; 
          border: none !important; 
        }
        .custom-description ul { 
          list-style-type: disc; 
          padding-left: 1.5rem; 
        }
        .custom-description ol { 
          list-style-type: decimal; 
          padding-left: 1.5rem; 
        }
        .jodit-editor-container :global(.jodit-container) { 
          border: none !important; 
          background: #f9fafb !important; 
          min-height: 300px; 
        }
        .jodit-editor-container :global(.jodit-workplace) { 
          background: #f9fafb !important; 
          min-height: 250px; 
        }
        .jodit-editor-container :global(.jodit-toolbar__box) { 
          background: #f9fafb !important; 
          border-bottom: 1px solid #eee !important; 
        }
        .jodit-editor-container :global(.jodit-wysiwyg) { 
          background: #f9fafb !important; 
          padding: 16px !important; 
          min-height: 200px; 
        }
      `}</style>
    </div>
  );
};

export default BecomeAffiliateMember;